import { useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { InfoIcon } from './icons/InfoIcon'

export interface InfoTooltipProps {
  children: ReactNode
  icon?: ReactNode
  width?: number
  placement?: 'above' | 'below' | 'left'
  color?: string
}

const VIEWPORT_MARGIN = 8

export function InfoTooltip({
  children,
  icon,
  width = 260,
  placement = 'above',
  color = 'var(--info)',
}: InfoTooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null)
  const [visible, setVisible] = useState(false)
  const [style, setStyle] = useState<CSSProperties | null>(null)

  useLayoutEffect(() => {
    if (!visible || !triggerRef.current) {
      return
    }
    const rect = triggerRef.current.getBoundingClientRect()

    // Side placement — opens to the LEFT of the icon, vertically centred on it. Used
    // where the icon sits at the right edge of the page (e.g. a right-most column
    // header) so the tooltip cannot run off-screen to the right.
    if (placement === 'left') {
      setStyle({
        position: 'fixed',
        top: rect.top + rect.height / 2,
        right: window.innerWidth - rect.left + 8,
        width,
        transform: 'translateY(-50%)',
      } as CSSProperties)
      return
    }

    // Default: right edge of tooltip aligns with right edge of the icon (opens left).
    let left = rect.right - width
    // Too close to the left edge of the viewport — flip to open rightward instead.
    if (left < VIEWPORT_MARGIN) {
      left = rect.left
    }
    // Never let either choice push the tooltip past the right edge of the viewport.
    const maxLeft = window.innerWidth - VIEWPORT_MARGIN - width
    if (left > maxLeft) {
      left = Math.max(VIEWPORT_MARGIN, maxLeft)
    }

    const arrowLeft = Math.min(Math.max(rect.left + rect.width / 2 - left - 5, 10), width - 20)

    const vertical: Record<string, number> = placement === 'below'
      ? { top: rect.bottom + 6 }
      : { bottom: window.innerHeight - rect.top + 6 }

    setStyle({
      position: 'fixed',
      left,
      width,
      ...vertical,
      ['--info-tooltip-arrow-left' as string]: `${arrowLeft}px`,
    } as CSSProperties)
  }, [visible, width, placement])

  return (
    <span
      ref={triggerRef}
      className="info-tooltip-trigger"
      style={{ color }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {icon ?? <InfoIcon />}
      {visible && style && (
        <span role="tooltip" className={`info-tooltip-bubble info-tooltip-${placement}`} style={style}>
          {children}
        </span>
      )}
    </span>
  )
}
